import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut, Wallet } from 'lucide-react';
import { useAuth } from '../App';
import BrandLogo from './BrandLogo';
import './Navbar.css';

const Navbar = ({ embedded = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logoutUser } = useAuth();
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    if (embedded) return undefined;

    const handleScroll = () => {
      if (!isMobileMenuOpen) {
        setIsScrolled(window.scrollY > 20);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMobileMenuOpen, embedded]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      // Save current scroll position
      scrollPositionRef.current = window.scrollY;
      
      // Apply styles to lock scroll
      document.documentElement.style.overflow = 'hidden';
      document.documentElement.style.height = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollPositionRef.current}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
    } else {
      // Remove styles
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
      
      // Restore scroll position
      if (scrollPositionRef.current > 0) {
        window.scrollTo(0, scrollPositionRef.current);
      }
    }
    
    return () => {
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';
      document.body.style.overflow = '';
      document.body.style.height = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';
    };
  }, [isMobileMenuOpen]);
  
  // Prevent touchmove on the backdrop
  const handleBackdropTouch = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);
  
  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Ledger', path: '/ledger' },
    { name: 'Staking', path: '/staking' },
    { name: 'Trading', path: '/trading' },
    { name: 'Contact', path: '/contact' },
  ];

  const isHomePage = location.pathname === '/';

  return (
    <>
      <header className={`navbar${embedded ? ' navbar-embedded' : ''}${!embedded && isScrolled ? ' scrolled' : ''}`}>
        <div className="container">
          <div className="navbar-inner">
            {/* Logo */}
            <Link to="/" className="navbar-logo hide-desktop">
              <BrandLogo height={36} />
            </Link>

            {/* Desktop Navigation */}
            {!isHomePage && (
              <nav className="navbar-nav hide-mobile">
                {navLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`nav-link ${location.pathname === link.path ? 'active' : ''}`}
                  >
                    {link.name}
                  </Link>
                ))}
              </nav>
            )}

            {/* Actions */}
            <div className="navbar-actions">
              {isHomePage ? (
                <>
                  <Link to="/staking" className="nav-btn-staking hide-mobile">
                    STAKING
                  </Link>
                  <Link to="/get-started" className="nav-btn-wallet hide-mobile" aria-label="Connect wallet">
                    <Wallet size={20} />
                  </Link>
                </>
              ) : user ? (
                <>
                  <Link to="/dashboard" className="btn btn-ghost hide-mobile">
                    <LayoutDashboard size={18} />
                    Dashboard
                  </Link>
                  <button onClick={handleLogout} className="btn btn-secondary hide-mobile">
                    <LogOut size={18} />
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-ghost hide-mobile">
                    Login
                  </Link>
                  <Link to="/get-started" className="btn btn-primary">
                    Get Started
                  </Link>
                </>
              )}
              
              {/* Mobile Menu Toggle */}
              <button 
                className="mobile-menu-toggle hide-desktop"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Backdrop - outside header to avoid overflow issues */}
      <div 
        className={`mobile-menu-backdrop ${isMobileMenuOpen ? 'active' : ''}`}
        onClick={closeMenu}
        onTouchMove={handleBackdropTouch}
        onTouchStart={handleBackdropTouch}
      />
      
      {/* Mobile Menu - outside header to avoid overflow issues */}
      <div 
        className={`mobile-menu ${isMobileMenuOpen ? 'open' : ''}`}
        onTouchMove={(e) => e.stopPropagation()}
      >
        <nav className="mobile-nav">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              className={`mobile-nav-link ${location.pathname === link.path ? 'active' : ''}`}
              onClick={closeMenu}
            >
              {link.name}
            </Link>
          ))}
          <div className="mobile-nav-divider"></div>
          {user ? (
            <>
              <Link to="/dashboard" className="mobile-nav-link" onClick={closeMenu}>
                <LayoutDashboard size={18} />
                Dashboard
              </Link>
              <button onClick={handleLogout} className="mobile-nav-link logout-btn">
                <LogOut size={18} />
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="mobile-nav-link" onClick={closeMenu}>Login</Link>
              <Link to="/get-started" className="btn btn-primary w-full" onClick={closeMenu}>
                Get Started
              </Link>
            </>
          )}
        </nav>
      </div>
    </>
  );
};

export default Navbar;
